import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Send, Loader2 } from "lucide-react";
import { SITE } from "../lib/site";
import Reveal from "../components/Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Field = ({ label, name, type = "text", value, onChange, required = false, textarea = false, ...rest }) => (
    <label className="block">
        <span className="block text-[10px] uppercase tracking-widest2 text-charcoal/60 mb-2">{label}{required && " *"}</span>
        {textarea ? (
            <textarea
                name={name}
                value={value}
                onChange={onChange}
                rows={5}
                required={required}
                data-testid={`contact-input-${name}`}
                className="w-full bg-transparent border-b border-line focus:border-forest focus:outline-none py-3 text-base text-forest placeholder:text-charcoal/30 transition-colors"
                {...rest}
            />
        ) : (
            <input
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={`contact-input-${name}`}
                className="w-full bg-transparent border-b border-line focus:border-forest focus:outline-none py-3 text-base text-forest placeholder:text-charcoal/30 transition-colors"
                {...rest}
            />
        )}
    </label>
);

const Contact = () => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
    const [submitting, setSubmitting] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Please share your name, email, and a message.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            toast.success("Message received. Our team will respond within one business day.");
            setForm({ name: "", email: "", phone: "", company: "", message: "" });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail) ? detail[0]?.msg || "Something went wrong" : (detail || "Something went wrong. Please try again.");
            toast.error(typeof msg === "string" ? msg : "Something went wrong. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="contact-page">
            <section className="pt-12 pb-12 lg:pt-20 lg:pb-16">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <Reveal>
                        <div className="text-xs uppercase tracking-widest2 text-copper mb-6">— Begin a conversation</div>
                        <h1 className="font-serif text-5xl lg:text-7xl font-light text-forest leading-[0.95] tracking-tight max-w-3xl text-balance">
                            Tell us what you're <span className="italic text-copper">building.</span>
                        </h1>
                    </Reveal>
                </div>
            </section>

            <section className="pb-32">
                <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
                    <div className="lg:col-span-5">
                        <Reveal>
                            <p className="text-lg text-charcoal/75 leading-relaxed">
                                Whether you need a single roll for a prototype or container loads for a production line,
                                our team will respond personally — usually within one business day.
                            </p>
                        </Reveal>

                        <div className="mt-12 space-y-8">
                            <Reveal delay={100}>
                                <div className="border-t border-line pt-6">
                                    <div className="text-xs uppercase tracking-widest2 text-copper mb-3 flex items-center gap-2">
                                        <Mail size={14} /> Email
                                    </div>
                                    <a href={`mailto:${SITE.email}`} data-testid="contact-email-link" className="font-serif text-3xl text-forest underline-grow">
                                        {SITE.email}
                                    </a>
                                </div>
                            </Reveal>
                            <Reveal delay={180}>
                                <div className="border-t border-line pt-6">
                                    <div className="text-xs uppercase tracking-widest2 text-copper mb-3 flex items-center gap-2">
                                        <Phone size={14} /> Phone
                                    </div>
                                    <div className="flex flex-col gap-2">
                                        <a href={`tel:${SITE.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="font-serif text-3xl text-forest underline-grow">
                                            {SITE.phone}
                                        </a>
                                        <a href={`tel:${SITE.phoneAlt.replace(/\s/g, "")}`} data-testid="contact-phone-alt-link" className="font-serif text-2xl text-forest/80 underline-grow">
                                            {SITE.phoneAlt}
                                        </a>
                                    </div>
                                </div>
                            </Reveal>
                            <Reveal delay={260}>
                                <div className="border-t border-line pt-6">
                                    <div className="text-xs uppercase tracking-widest2 text-copper mb-3 flex items-center gap-2">
                                        <MapPin size={14} /> Address
                                    </div>
                                    <div className="font-serif text-2xl text-forest leading-snug">
                                        {SITE.address}
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <Reveal delay={120}>
                            <form onSubmit={onSubmit} data-testid="contact-form" className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-7">
                                <Field label="Your name" name="name" value={form.name} onChange={onChange} required placeholder="Full name" />
                                <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required placeholder="you@company.com" />
                                <Field label="Phone" name="phone" value={form.phone} onChange={onChange} placeholder="+1 555 123 4567" />
                                <Field label="Company" name="company" value={form.company} onChange={onChange} placeholder="Your business" />
                                <div className="sm:col-span-2">
                                    <Field label="Project details" name="message" value={form.message} onChange={onChange} required textarea placeholder="Tell us about quantities, profiles, target colours, and timelines…" />
                                </div>
                                <div className="sm:col-span-2 flex items-center justify-between gap-6 mt-2">
                                    <p className="text-xs text-charcoal/60 max-w-sm">
                                        We respect your privacy. Your details are used only to respond to your enquiry.
                                    </p>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="contact-submit-btn"
                                        className="inline-flex items-center gap-3 px-7 py-4 bg-forest text-bone hover:bg-charcoal transition-colors disabled:opacity-50"
                                    >
                                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
                                        {submitting ? "Sending…" : "Send message"}
                                    </button>
                                </div>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Contact;
